import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { translateBoardType } from "@/utils/boardTypeTranslations";
import { getBoardTypeIcon, getBoardTypeBadgeStyle } from "@/utils/boardTypeIcons";
import { IterpecHotelRoom } from "@/types/iterpec";
import { useTranslation as useHotelTranslation } from "@/hooks/useTranslation";
import { useTranslation } from "react-i18next";
import { usePrice } from "@/hooks/usePrice";
import { useLocale } from "@/contexts/LocaleContext";
import { cleanHtmlText } from "@/utils/textUtils";
import { useState } from "react";
import { ShoppingCart, Bed, Info, Users, CheckCircle, XCircle, ChevronDown, ChevronUp, Coffee, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCart } from "@/hooks/useCart";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface RoomCardProps {
  room: IterpecHotelRoom;
  roomPrice: number;
  pricePerNight: number;
  cancellationPolicy: string;
  cancellationType: string;
  numNights: number;
  onBookRoom: (room: IterpecHotelRoom) => void;
  hotelId?: number;
  hotelName?: string;
  checkIn?: string;
  checkOut?: string;
  searchId?: string | null;
  isBooking?: boolean;
}

export const RoomCard = ({
  room,
  roomPrice,
  pricePerNight,
  cancellationPolicy,
  cancellationType,
  numNights,
  onBookRoom,
  hotelId,
  hotelName,
  checkIn,
  checkOut,
  searchId,
  isBooking = false,
}: RoomCardProps) => {
  const { t, i18n } = useTranslation();
  const { locale } = useLocale();
  const { format: formatPrice, isConverting } = usePrice();
  const [isMoreInfoExpanded, setIsMoreInfoExpanded] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  
  const targetLang = i18n.language.startsWith('pt') ? 'pt-BR' : i18n.language.startsWith('en') ? 'en' : 'es';
  // Translate content for non-Portuguese users, using auto-detection for source language
  const needsTranslation = !i18n.language.startsWith('pt');
  
  const cleanMoreInfo = room.MoreInformation ? cleanHtmlText(room.MoreInformation) : undefined;
  const { translatedText: translatedMoreInfo } = useHotelTranslation(
    needsTranslation ? cleanMoreInfo : undefined,
    'auto',
    targetLang
  );
  const displayMoreInfo = needsTranslation ? (translatedMoreInfo || cleanMoreInfo) : cleanMoreInfo;
  
  const { translatedText: translatedBedDescription } = useHotelTranslation(
    needsTranslation ? room.BedDescription : undefined,
    'auto',
    targetLang
  );
  const displayBedDescription = needsTranslation ? (translatedBedDescription || room.BedDescription) : room.BedDescription;
  
  const { toast } = useToast();
  const { addItem } = useCart();

  const isRefundable = cancellationType === "Reembolsable" || cancellationType === "Refundable" || cancellationType === "Reembolsável";

  // Check if breakfast is included in the board type
  const hasBreakfastIncluded = (() => {
    if (!room.BoardDescription) return false;
    const board = room.BoardDescription.toLowerCase();
    // Keywords that indicate breakfast is included
    const breakfastIncludedKeywords = [
      'breakfast', 'desayuno', 'café da manhã', 'cafe da manha',
      'bed and breakfast', 'half board', 'full board', 'all inclusive',
      'media pensión', 'media pension', 'pensión completa', 'pension completa',
      'meia pensão', 'meia pensao', 'pensão completa', 'pensao completa',
      'todo incluido', 'tudo incluído', 'tudo incluido'
    ];
    // Keywords that indicate NO breakfast
    const noBreakfastKeywords = [
      'no breakfast', 'sin desayuno', 'sem café', 'sem cafe',
      'room only', 'solo alojamiento', 'apenas acomodação', 'apenas acomodacao',
      'no meals', 'sin comidas', 'sem refeições', 'sem refeicoes'
    ];
    // Check if any "no breakfast" keyword is present
    if (noBreakfastKeywords.some(keyword => board.includes(keyword))) {
      return false;
    }
    // Check if any "breakfast included" keyword is present
    return breakfastIncludedKeywords.some(keyword => board.includes(keyword));
  })();

  const handleAddToCart = async () => {
    setIsAddingToCart(true);
    try {
      if (!hotelId || !hotelName || !room.Id) {
        throw new Error(t('errors.generic'));
      }

      const { data: productData, error: productError } = await supabase.functions.invoke(
        'save-hotel-product',
        {
          body: {
            hotelId,
            roomId: room.Id.toString(),
            roomDescription: room.RoomDescription || 'Quarto',
            bedDescription: room.BedDescription || null,
            boardDescription: room.BoardDescription || null,
            priceEur: roomPrice,
            hotelName,
          },
        }
      );

      if (productError || !productData?.success) {
        throw new Error(t('errors.generic'));
      }

      const cancellationPolicyData = room.CancellationPolicies?.[0];
      const cancellationStartDate = cancellationPolicyData?.StartDate || null;

      await addItem({
        productId: productData.productId,
        bookingDetails: {
          type: 'hotel',
          hotelId,
          hotelName,
          hotelAddress: '',
          roomId: room.Id.toString(),
          roomName: room.RoomDescription,
          roomDescription: room.RoomDescription,
          bedDescription: room.BedDescription || null,
          boardType: room.BoardDescription,
          boardDescription: room.BoardDescription,
          checkIn,
          checkOut,
          numNights: numNights.toString(),
          priceEur: roomPrice,
          propertyTaxesEur: room.PropertyTaxesEur,
          cancellationPolicies: room.CancellationPolicies,
          cancellationType,
          cancellationPolicy,
          cancellationStartDate,
        },
        searchToken: searchId || undefined,
      });

      toast({
        title: t('cart.itemAdded'),
        description: t('hotels.roomSelected'),
      });
      
      // Show success animation
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 2000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('errors.generic'),
        variant: "destructive",
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow border-l-4 border-l-primary/20 hover:border-l-primary">
      <CardContent className="p-0">
        <div className="flex flex-col md:flex-row md:items-center">
          {/* Room Info */}
          <div className="flex-1 p-4 md:pr-4 border-b md:border-b-0 md:border-r border-border/50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base leading-tight mb-2">{room.RoomDescription}</h3>
                
                {/* Bed Info */}
                {displayBedDescription && (
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1.5">
                    <Bed className="h-3.5 w-3.5 shrink-0" />
                    <span>{displayBedDescription}</span>
                  </div>
                )}
                
                {/* Board Type */}
                {room.BoardDescription && (
                  <div className="mb-2">
                    {(() => {
                      const translatedBoard = translateBoardType(room.BoardDescription, locale);
                      const Icon = getBoardTypeIcon(translatedBoard);
                      const badgeStyle = getBoardTypeBadgeStyle(translatedBoard);
                      return (
                        <Badge className={`${badgeStyle} text-xs px-2 py-0.5 font-medium`}>
                          <Icon className="h-3 w-3 mr-1" />
                          {translatedBoard}
                        </Badge>
                      );
                    })()}
                  </div>
                )}

                {/* More Info Collapsible */}
                {displayMoreInfo && (
                  <Collapsible open={isMoreInfoExpanded} onOpenChange={setIsMoreInfoExpanded} className="mt-2">
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-auto p-0 text-primary hover:text-primary/80 text-xs">
                        <Info className="h-3 w-3 mr-1" />
                        {isMoreInfoExpanded ? t('common.seeLess') : t('hotels.moreInfo')}
                        {isMoreInfoExpanded ? <ChevronUp className="h-3 w-3 ml-0.5" /> : <ChevronDown className="h-3 w-3 ml-0.5" />}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-1.5">
                      <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded-md">
                        {displayMoreInfo}
                      </p>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>

              {/* Badges for mobile */}
              <div className="flex flex-wrap gap-1.5 sm:flex-col sm:items-end">
                {/* Breakfast badge - hidden on mobile to avoid duplication with board type badge */}
                {hasBreakfastIncluded && (
                  <Badge className="hidden md:flex bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20 text-xs px-2 py-0.5">
                    <Coffee className="h-3 w-3 mr-1" />
                    {t('room.breakfastIncluded')}
                  </Badge>
                )}
                {/* Cancellation for mobile */}
                <div className="flex flex-col items-start sm:items-end gap-0.5 md:hidden">
                  <Badge 
                    variant={isRefundable ? "default" : "destructive"} 
                    className={`text-xs px-2 py-0.5 ${isRefundable ? 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20' : ''}`}
                  >
                    {isRefundable ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                    {cancellationType}
                  </Badge>
                  {cancellationPolicy !== cancellationType && (
                    <span className="text-xs text-muted-foreground">{cancellationPolicy}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Cancellation Policy - Vertically centered */}
          <div className="hidden md:flex items-center justify-center px-4 border-r border-border/50">
            <div className="flex flex-col items-center gap-0.5 text-center">
              <Badge 
                variant={isRefundable ? "default" : "destructive"} 
                className={`text-xs px-2 py-0.5 ${isRefundable ? 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20' : ''}`}
              >
                {isRefundable ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                {cancellationType}
              </Badge>
              {cancellationPolicy !== cancellationType && (
                <span className="text-xs text-muted-foreground max-w-[140px]">{cancellationPolicy}</span>
              )}
            </div>
          </div>

          {/* Price & Actions */}
          <div className="flex items-center justify-between md:justify-end gap-4 p-4 bg-muted/30 md:min-w-[280px]">
            {/* Price */}
            <div className="text-left md:text-right">
              <div className="flex items-baseline gap-1 md:justify-end">
                <span className="text-xl lg:text-2xl font-bold text-primary">
                  {isConverting ? (
                    <Skeleton className="h-6 w-20 inline-block" />
                  ) : (
                    formatPrice(pricePerNight)
                  )}
                </span>
                <span className="text-sm text-muted-foreground">/{t('common.night')}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {isConverting ? (
                  <Skeleton className="h-3 w-24 inline-block" />
                ) : (
                  <>
                    {formatPrice(roomPrice)} {t('common.total')} • {numNights} {numNights === 1 ? t('common.night') : t('common.nights')}
                  </>
                )}
              </div>
              {room.PropertyTaxesEur && room.PropertyTaxesEur > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  + {formatPrice(room.PropertyTaxesEur)} {t('hotels.localTaxes')}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button 
                onClick={handleAddToCart}
                variant={addedToCart ? "default" : "outline"}
                size="icon"
                className={`h-10 w-10 shrink-0 transition-all duration-300 ${
                  addedToCart 
                    ? 'bg-green-500 hover:bg-green-500 border-green-500 text-white animate-scale-in' 
                    : ''
                }`}
                disabled={isAddingToCart || isBooking}
                title={t('common.addToCart')}
              >
                {isAddingToCart ? (
                  <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                ) : addedToCart ? (
                  <Check className="h-4 w-4 animate-scale-in" />
                ) : (
                  <div className="flex items-center gap-0.5">
                    <span className="text-base font-bold leading-none">+</span>
                    <ShoppingCart className="h-3.5 w-3.5" />
                  </div>
                )}
              </Button>
              <Button 
                onClick={() => onBookRoom(room)}
                className="h-10 px-4"
                disabled={isAddingToCart || isBooking}
              >
                {isBooking ? t('common.loading') : t('common.reserve')}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
